import React, { useEffect, useState } from 'react';
import {
    Table, TableBody, TableCell, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Container, Typography, Box, Grid, Paper, Button,
    TextField, FormControl, InputLabel, Select, MenuItem,
    IconButton, Switch
} from '@mui/material';
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from 'react-router-dom';
import { db } from "../../firebase";
import { collection, query, where, getDocs, getDoc, doc, deleteDoc, updateDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { getFacilitiesByTenant, createFacility, updateFacility, deleteFacility } from '../../db/facilityApi';
import { Edit, Delete, ArrowForward, Visibility } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { addOrUpdateUser } from '../../db/userApi';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from '@mui/icons-material/Close';
import { Snackbar, Alert } from "@mui/material";

const TenantAdminDashboard = () => {
    const navigate = useNavigate();
    const { user, isAppAdmin } = useAuth();
    const [facilityCount, setFacilityCount] = useState(0);
    const [selectedFacilityId, setSelectedFacilityId] = useState('');
    const [facilities, setFacilities] = useState([]);
    const [tenantFacilityAdminCount, setTenantFacilityAdminCount] = useState(0);
    const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);
    const [facilityDialogMode, setFacilityDialogMode] = useState('add');
    const [editingFacilityId, setEditingFacilityId] = useState(null);
    const [generatedFacilityId, setGeneratedFacilityId] = useState('');
    const [facilityForm, setFacilityForm] = useState({
        shortName: '',
        fullName: '',
        address: '',
        mapCoordinates: { lat: '', lng: '' },
        contact: { name: '', email: '', mobile: '' },
        facilityType: 200,
        status: 'active'
    });
    const [facilityAdmins, setFacilityAdmins] = useState([]);
    const [adminForm, setAdminForm] = useState({
        name: '',
        email: '',
        mobile: ''
    });
    const [tenantType, setTenantType] = useState(null);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const viewFacilityId = queryParams.get('viewFacilityId');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        id: null
    });

    // Tenant Facility Admin Count
    useEffect(() => {
        if (!isAppAdmin() || !user?.tenantId) return;

        const facilityAdminsQuery = query(
            collection(db, 'users'),
            where('tenantId', '==', user.tenantId),
            where('role', '==', 'facilityadmin')
        );

        const unsubscribe = onSnapshot(facilityAdminsQuery, (snapshot) => {
            setTenantFacilityAdminCount(snapshot.size);
        });

        return () => unsubscribe();
    }, [user, isAppAdmin]);

    // Load Tenant Type
    useEffect(() => {
        const loadTenantType = async () => {
            if (!user?.tenantId) return;

            const tenantSnap = await getDocs(
                query(collection(db, "tenantmaster"), where("tenantId", "==", user.tenantId))
            );

            if (!tenantSnap.empty) {
                setTenantType(tenantSnap.docs[0].data().tenantType);
            }
        };

        loadTenantType();
    }, [user]);

    // Load Facilities for Tenant Admin
    useEffect(() => {
        const loadFacilities = async () => {
            if (!isAppAdmin() || !user?.tenantId) return;

            try {
                let tenantId = user.tenantId;
                if (!tenantId && user.userID === "RM8WhlmV48bX9Zh9LJf5HDLuBh53") {
                    tenantId = "KYHI6vp8jlEe43lvzYgv";
                }
                if (!tenantId) return;

                const facilityData = await getFacilitiesByTenant(tenantId);
                setFacilities(facilityData);
                setFacilityCount(facilityData.length);
                if (facilityData.length > 0) {
                    setSelectedFacilityId(facilityData[0].id);
                }
            } catch (err) {
                console.error('Error loading facilities:', err);
            }
        };

        loadFacilities();
    }, [user]);

    // Access Check
    if (!user || !isAppAdmin()) {
        return null;
    }

    const handleFacilitySubmit = async () => {
        const facilityData = {
            ...facilityForm,
            tenantId: user.tenantId,
            facilityType: tenantType === 300 ? facilityForm.facilityType : tenantType
        };

        if (!facilityForm.shortName.trim() || !facilityForm.fullName.trim()) {
            setSnackbar({
                open: true,
                message: "Please fill required fields",
                severity: "warning"
            });
            return;
        }

        try {
            let facilityId = editingFacilityId;

            if (!editingFacilityId) {
                facilityId = await createFacility(user.tenantId, facilityData, user.uid);
                setGeneratedFacilityId(facilityId);
            } else {
                await updateFacility(user.tenantId, editingFacilityId, facilityForm);
            }

            // Create facility admins (only in add mode)
            if (facilityAdmins.length > 0) {
                for (const admin of facilityAdmins) {
                    // If admin already exists → skip
                    if (admin.id) continue;

                    // Check if email already exists
                    const emailSnap = await getDocs(query(
                        collection(db, 'users'),
                        where('userEmailId', '==', admin.email)
                    ));

                    if (!emailSnap.empty) {
                        const existingUser = emailSnap.docs[0].data();
                        alert(`Email already exists with role: ${existingUser.role}`);
                        continue;
                    }

                    await addOrUpdateUser({
                        userName: admin.name,
                        userEmailId: admin.email,
                        userMobileNo: admin.mobile,
                        role: 'facilityadmin',
                        facilityId
                    }, user.tenantId, null);
                }
            }

            // Reset
            setFacilityDialogOpen(false);
            setEditingFacilityId(null);
            setFacilityAdmins([]);
            setAdminForm({ name: '', email: '', mobile: '' });

            setFacilityForm({
                shortName: '',
                fullName: '',
                address: '',
                mapCoordinates: { lat: '', lng: '' },
                contact: { name: '', email: '', mobile: '' },
                status: 'active'
            });

            // Refresh facilities
            const updatedFacilities = await getFacilitiesByTenant(user.tenantId);
            setFacilities(updatedFacilities);
        } catch (error) {
            alert(error.message);
            console.error(error);
        }
    };

    return (
        <Box sx={{
            minHeight: "100vh",
            background: "#fefce8",
            py: 4
        }}>
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography sx={{
                            fontSize: "2rem",
                            fontWeight: 900,
                            fontFamily: "'Unbounded', sans-serif",
                            color: "#1c1400"
                        }}>
                            Admin Dashboard
                        </Typography>

                        <Typography sx={{ color: "#8a7a40", mt: 0.5 }}>
                            Welcome, {user?.userName || user?.email}
                        </Typography>

                        <Typography sx={{
                            color: "#d32f2f",
                            fontWeight: 500,
                            mt: 1
                        }}>
                            You have access to facility and user management for your Organization.
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        sx={{
                            background: "#1c1400",
                            color: "#fde047",
                            fontWeight: 700,
                            px: 3,
                            borderRadius: "8px",
                            "&:hover": {
                                background: "#f59e0b",
                                color: "#1c1400"
                            }
                        }}
                        onClick={() => {
                            setFacilityDialogMode('add');
                            setEditingFacilityId(null);
                            setGeneratedFacilityId('Auto Generated After Save');
                            setFacilityForm({
                                shortName: '',
                                fullName: '',
                                address: '',
                                mapCoordinates: { lat: '', lng: '' },
                                contact: { name: '', email: '', mobile: '' },
                                status: 'active'
                            });
                            setFacilityAdmins([]);
                            setAdminForm({ name: '', email: '', mobile: '' });
                            setFacilityDialogOpen(true);
                        }}
                    >
                        + Add Facility
                    </Button>
                </Box>

                {/* Tenant Admin Analytics */}
                <Paper sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: "16px",
                    border: "2px solid #f0d060",
                    background: "#ffffff"
                }}>
                    <Typography variant="h6" gutterBottom>
                        Facility Analytics
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6}>
                            <Typography sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: "#1c1400",
                                letterSpacing: "0.02em"
                            }}>No. of Facilities</Typography>
                            <Typography sx={{
                                fontSize: "2rem",
                                fontWeight: 800,
                                color: "#1c1400"
                            }}>{facilities.length}</Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Typography sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: "#1c1400",
                                letterSpacing: "0.02em"
                            }}>No. of Facility Admins</Typography>
                            <Typography sx={{
                                fontSize: "2rem",
                                fontWeight: 800,
                                color: "#1c1400"
                            }}>{tenantFacilityAdminCount}</Typography>
                        </Grid>
                    </Grid>

                    {/* Facilities Table */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>Facilities</Typography>

                        <Table sx={{
                            mt: 2,
                            border: "1px solid #f0d060",
                            borderRadius: "12px",
                            overflow: "hidden"
                        }}>
                            <TableHead sx={{ background: "#fef3c7" }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Short Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Full Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Address</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {facilities.map((f) => (
                                    <TableRow key={f.id}>
                                        <TableCell>{f.shortName || '-'}</TableCell>
                                        <TableCell>{f.fullName || f.facilityName || '-'}</TableCell>
                                        <TableCell>
                                            {f.facilityType === 100
                                                ? "Residential"
                                                : f.facilityType === 200
                                                    ? "Commercial"
                                                    : "-"}
                                        </TableCell>
                                        <TableCell>{f.address || '-'}</TableCell>

                                        <TableCell>
                                            <Switch
                                                checked={f.status === 'active'}
                                                onChange={async (e) => {
                                                    const newStatus = e.target.checked ? 'active' : 'inactive';
                                                    await updateFacility(user.tenantId, f.id, { status: newStatus });
                                                    const updatedFacilities = await getFacilitiesByTenant(user.tenantId);
                                                    setFacilities(updatedFacilities);
                                                }}
                                                color="warning"
                                            />
                                            {f.status}
                                        </TableCell>

                                        <TableCell>
                                            <IconButton
                                                color="info"
                                                onClick={async () => {
                                                    setFacilityForm({
                                                        shortName: f.shortName || '',
                                                        fullName: f.fullName || f.facilityName || '',
                                                        address: f.address || '',
                                                        mapCoordinates: f.mapCoordinates || { lat: '', lng: '' },
                                                        contact: f.contact || { name: '', email: '', mobile: '' },
                                                        facilityType: f.facilityType || 200,
                                                        status: f.status || 'active'
                                                    });

                                                    const adminSnap = await getDocs(query(
                                                        collection(db, 'users'),
                                                        where('facilityId', '==', f.id),
                                                        where('role', '==', 'facilityadmin')
                                                    ));

                                                    const existingAdmins = adminSnap.docs.map(doc => {
                                                        const data = doc.data();
                                                        return {
                                                            id: doc.id,
                                                            name: data.userName || data.name || '',
                                                            email: data.userEmailId || data.userEmailID || data.email || '',
                                                            mobile: data.userMobileNo || data.mobile || ''
                                                        };
                                                    });

                                                    setFacilityAdmins(existingAdmins);
                                                    setEditingFacilityId(f.id);
                                                    setFacilityDialogMode('view');
                                                    setFacilityDialogOpen(true);
                                                }}
                                            >
                                                <Visibility sx={{ color: "#0284c7" }} />
                                            </IconButton>

                                            <IconButton
                                                color="primary"
                                                onClick={async () => {
                                                    setFacilityForm({
                                                        shortName: f.shortName || '',
                                                        fullName: f.fullName || f.facilityName || '',
                                                        address: f.address || '',
                                                        mapCoordinates: f.mapCoordinates || { lat: '', lng: '' },
                                                        contact: f.contact || { name: '', email: '', mobile: '' },
                                                        facilityType: f.facilityType || 200,
                                                        status: f.status || 'active'
                                                    });

                                                    const adminSnap = await getDocs(query(
                                                        collection(db, 'users'),
                                                        where('facilityId', '==', f.id),
                                                        where('role', '==', 'facilityadmin')
                                                    ));

                                                    const existingAdmins = adminSnap.docs.map(doc => {
                                                        const data = doc.data();
                                                        return {
                                                            id: doc.id,
                                                            name: data.userName || data.name || '',
                                                            email: data.userEmailId || data.userEmailID || data.email || '',
                                                            mobile: data.userMobileNo || data.mobile || ''
                                                        };
                                                    });

                                                    setFacilityAdmins(existingAdmins);
                                                    setEditingFacilityId(f.id);
                                                    setFacilityDialogMode('edit');
                                                    setFacilityDialogOpen(true);
                                                }}
                                            >
                                                <Edit sx={{ color: "#f59e0b" }} />
                                            </IconButton>

                                            <IconButton
                                                onClick={() => setDeleteDialog({ open: true, id: f.id })}
                                            >
                                                <Delete sx={{ color: "#dc2626" }} />
                                            </IconButton>

                                            <IconButton
                                                color="secondary"
                                                onClick={() => navigate(`/facility-dashboard?viewFacilityId=${f.id}`)}
                                            >
                                                <ArrowForward sx={{ color: "#7c3aed" }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {facilities.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No facilities found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Paper>

                <Box sx={{ mt: 4 }}>
                    <Paper sx={{
                        p: 3,
                        borderRadius: "12px",
                        background: "#fffbea",
                        border: "1px solid #f0d060"
                    }}>
                        <Typography variant="body2" color="textSecondary">
                            As an AppAdmin, you can manage facilities, users, and handle visitor requests.
                        </Typography>
                    </Paper>
                </Box>

                {/* Facility Dialog */}
                <Dialog
                    open={facilityDialogOpen}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: "18px",
                            p: 2
                        }
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', fontWeight: 700, color: "#1c1400", justifyContent: 'space-between', alignItems: 'center' }}>
                        {facilityDialogMode === 'edit' ? 'Edit Facility' : facilityDialogMode === 'view' ? 'View Facility' : 'Add Facility'}
                        <IconButton onClick={() => setFacilityDialogOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Facility ID"
                                    value={facilityDialogMode === 'edit' ? editingFacilityId : generatedFacilityId || 'Will be generated after save'}
                                    disabled
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Short Name"
                                    value={facilityForm.shortName}
                                    disabled={facilityDialogMode === 'edit' || facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({ ...facilityForm, shortName: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    value={facilityForm.fullName}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({ ...facilityForm, fullName: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {tenantType === 300 && (
                                    <TextField
                                        select
                                        fullWidth
                                        label="Facility Type"
                                        value={facilityForm.facilityType}
                                        disabled={facilityDialogMode === 'view'}
                                        onChange={(e) => setFacilityForm({ ...facilityForm, facilityType: Number(e.target.value) })}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "10px",
                                                backgroundColor: "#fff",
                                                "& fieldset": {
                                                    borderColor: "#f0d060"
                                                },
                                                "&:hover fieldset": {
                                                    borderColor: "#f59e0b"
                                                },
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "#f59e0b"
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value={100}>Residential</MenuItem>
                                        <MenuItem value={200}>Commercial</MenuItem>
                                    </TextField>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    value={facilityForm.address}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({ ...facilityForm, address: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Latitude (Optional)"
                                    value={facilityForm.mapCoordinates?.lat || ''}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({
                                        ...facilityForm,
                                        mapCoordinates: { ...facilityForm.mapCoordinates, lat: e.target.value }
                                    })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Longitude (Optional)"
                                    value={facilityForm.mapCoordinates?.lng || ''}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({
                                        ...facilityForm,
                                        mapCoordinates:
                                            { ...facilityForm.mapCoordinates, lng: e.target.value }
                                    })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Status"
                                    value={facilityForm.status}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({ ...facilityForm, status: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{
                                    fontWeight: 700,
                                    color: "#1c1400",
                                    mt: 2,
                                    mb: 1
                                }}>
                                    Facility Point of Contact
                                </Typography>
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Name"
                                    value={facilityForm.contact?.name || ''}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({
                                        ...facilityForm,
                                        contact: { ...facilityForm.contact, name: e.target.value }
                                    })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Email"
                                    value={facilityForm.contact?.email || ''}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({
                                        ...facilityForm,
                                        contact: { ...facilityForm.contact, email: e.target.value }
                                    })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Mobile"
                                    value={facilityForm.contact?.mobile || ''}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setFacilityForm({
                                        ...facilityForm,
                                        contact: { ...facilityForm.contact, mobile: e.target.value }
                                    })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{
                                    fontWeight: 700,
                                    color: "#1c1400",
                                    mt: 2,
                                    mb: 1
                                }}>
                                    Facility Admins
                                </Typography>
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Admin Name"
                                    value={adminForm.name}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Admin Email"
                                    value={adminForm.email}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label="Admin Mobile"
                                    value={adminForm.mobile}
                                    disabled={facilityDialogMode === 'view'}
                                    onChange={(e) => setAdminForm({ ...adminForm, mobile: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: "#fff",
                                            "& fieldset": {
                                                borderColor: "#f0d060"
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "#f59e0b"
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "#f59e0b"
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                {facilityDialogMode !== 'view' && (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            background: "#1c1400",
                                            color: "#fde047",
                                            px: 3,
                                            borderRadius: "8px",
                                            "&:hover": {
                                                background: "#f59e0b",
                                                color: "#1c1400"
                                            }
                                        }}
                                        onClick={() => {
                                            if (!adminForm.name || !adminForm.email) {
                                                setSnackbar({
                                                    open: true,
                                                    message: "Admin Name and Email required",
                                                    severity: "warning"
                                                });
                                                return;
                                            }
                                            setFacilityAdmins([...facilityAdmins, adminForm]);
                                            setAdminForm({ name: '', email: '', mobile: '' });
                                        }}
                                    >
                                        + Add Facility Admin
                                    </Button>
                                )}
                            </Grid>

                            {facilityAdmins.length > 0 && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography sx={{
                                        fontWeight: 700,
                                        color: "#1c1400",
                                        mb: 1
                                    }}>
                                        Added Facility Admins
                                    </Typography>

                                    <Table sx={{
                                        mt: 2,
                                        border: "1px solid #f0d060",
                                        borderRadius: "12px",
                                        overflow: "hidden"
                                    }}>
                                        <TableHead sx={{ background: "#fef3c7" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Facility Admin Name</TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: "#1c1400" }}>Email</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 600, color: "#1c1400" }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {facilityAdmins.map((admin, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{admin.name}</TableCell>
                                                    <TableCell>{admin.email}</TableCell>
                                                    <TableCell align="center">
                                                        {facilityDialogMode !== 'view' && (
                                                            <IconButton
                                                                color="error"
                                                                size="small"
                                                                onClick={async () => {
                                                                    const adminToDelete = facilityAdmins[index];
                                                                    if (adminToDelete.id) {
                                                                        await deleteDoc(doc(db, 'users', adminToDelete.id));
                                                                    }
                                                                    const updated = [...facilityAdmins];
                                                                    updated.splice(index, 1);
                                                                    setFacilityAdmins(updated);
                                                                }}
                                                            >
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        {facilityDialogMode === 'view' ? (
                            <Button variant="contained"
                                sx={{
                                    background: "#1c1400",
                                    color: "#fde047",
                                    px: 3,
                                    borderRadius: "8px",
                                    "&:hover": {
                                        background: "#f59e0b",
                                        color: "#1c1400"
                                    }
                                }}
                                onClick={() => setFacilityDialogOpen(false)}
                            >
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        borderColor: "#d1d5db",
                                        color: "#374151",
                                        px: 3,
                                        borderRadius: "8px",
                                        fontWeight: 600,
                                        "&:hover": {
                                            borderColor: "#9ca3af",
                                            backgroundColor: "#f9fafb"
                                        }
                                    }}
                                    onClick={() => setFacilityDialogOpen(false)}
                                >
                                    Cancel
                                </Button>
                                <Button variant="contained"
                                    sx={{
                                        background: "#1c1400",
                                        color: "#fde047",
                                        px: 3,
                                        borderRadius: "8px",
                                        "&:hover": {
                                            background: "#f59e0b",
                                            color: "#1c1400"
                                        }
                                    }}
                                    onClick={handleFacilitySubmit}
                                >
                                    {facilityDialogMode === 'edit' ? 'Update' : 'Create'}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={deleteDialog.open}
                    onClose={() => setDeleteDialog({ open: false, id: null })}
                    PaperProps={{
                        sx: { borderRadius: "16px", p: 1 }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        Delete Facility?
                    </DialogTitle>

                    <DialogContent>
                        <Typography>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant="outlined"
                            sx={{
                                borderColor: "#d1d5db",
                                color: "#374151",
                                px: 3,
                                borderRadius: "8px",
                                fontWeight: 600,
                                "&:hover": {
                                    borderColor: "#9ca3af",
                                    backgroundColor: "#f9fafb"
                                }
                            }}
                            onClick={() => setDeleteDialog({ open: false, id: null })}>
                            Cancel
                        </Button>

                        <Button
                            color="error"
                            variant="contained"
                            onClick={async () => {
                                await deleteFacility(user.tenantId, deleteDialog.id);

                                const updated = await getFacilitiesByTenant(user.tenantId);
                                setFacilities(updated);

                                setDeleteDialog({ open: false, id: null });

                                setSnackbar({
                                    open: true,
                                    message: "Facility deleted successfully",
                                    severity: "success"
                                });
                            }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ borderRadius: "10px" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TenantAdminDashboard;